// Projection de trésorerie : ce que le compte devrait devenir.
//
// Le suivi bancaire dit où on EN EST — des mouvements réels, pointés un à un.
// Cette projection dit où on VA : elle repart du dernier solde constaté, y
// ajoute les encaissements que les chantiers placés à l'agenda laissent
// attendre, en retire les charges qui restent à payer, et déroule le solde mois
// par mois jusqu'à la fin de l'année.
//
// Deux règles tiennent tout le module :
//
//   1. RIEN d'estimé ne se mélange à du constaté. Le point de départ est le
//      solde de la banque à une date précise ; tout ce qui vient après est
//      marqué comme projeté, et l'écran le dit.
//
//   2. Dans le doute, on n'ajoute PAS d'argent. Un encaissement dont la date
//      supposée est déjà passée au regard du relevé n'entre pas dans le calcul :
//      il est vraisemblablement DÉJÀ sur le compte, et le compter une seconde
//      fois gonflerait la trésorerie. Il est listé à part, à l'utilisateur de
//      trancher. Se tromper en croyant avoir moins d'argent coûte une inquiétude ;
//      se tromper dans l'autre sens coûte un découvert.
//
// Fonctions PURES : pas d'horloge, pas de réseau. Tout arrive en paramètre,
// donc tout est testable à n'importe quelle date.

const JOUR_MS = 86_400_000;

/** Jour ISO (yyyy-mm-dd) d'un horodatage en millisecondes, en UTC. */
function jourIso(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/**
 * Décale une date ISO d'un nombre de jours, en UTC.
 *
 * L'arithmétique se fait en millisecondes depuis l'époque : additionner des
 * jours sur une date locale ferait sauter (ou répéter) une journée aux
 * changements d'heure de mars et d'octobre.
 */
export function ajouterJoursIso(iso: string, jours: number): string {
  const t = Date.parse(`${iso.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(t)) return iso.slice(0, 10);
  return jourIso(t + jours * JOUR_MS);
}

const anneeDe = (iso: string): number => Number(iso.slice(0, 4));
/** Mois 0-11 d'une date ISO. */
const moisDe = (iso: string): number => Number(iso.slice(5, 7)) - 1;

/** Un chantier placé à l'agenda, réduit à ce qui sert à projeter. */
export interface ChantierProjetable {
  chantierId: string;
  numero: string;
  libelle: string;
  client: string;
  /** Fin de la dernière intervention placée (date ou horodatage ISO). */
  finIntervention: string;
  /** Montant engagé sur le chantier, hors taxes. 0 si le CRM l'ignore. */
  montantHt: number;
}

/**
 * `attendu` compte dans la projection ; `encaisse` (l'argent est déjà arrivé)
 * et `exclu` (chantier abandonné, litige) en sortent, sans disparaître.
 */
export type EtatEncaissement = 'attendu' | 'encaisse' | 'exclu';

/** Ce que l'utilisateur a corrigé à la main sur une ligne. */
export interface AjustementEncaissement {
  etat: EtatEncaissement;
  /** Date d'encaissement forcée (yyyy-mm-dd) ; null = calculée. */
  datePrevue: string | null;
  /** Montant forcé, tel qu'attendu sur le compte ; null = calculé. */
  montantPrevu: number | null;
  note: string | null;
}

export interface ReglesProjection {
  /** Jours entre la fin du chantier et l'arrivée de l'argent sur le compte. */
  delaiJours: number;
  /** TVA ajoutée au montant HT pour obtenir ce que le client règle (0.1 = 10 %). */
  tauxTva: number;
  /** Part supposée déjà réglée en acompte, donc à ne pas réencaisser (0.3 = 30 %). */
  partAcompte: number;
}

/**
 * Valeurs de départ, volontairement prudentes :
 * 30 jours est le délai de règlement usuel, 10 % la TVA de la rénovation de
 * logements de plus de deux ans, et 0 % d'acompte déduit — le CRM ne VOIT pas
 * les acomptes, qui sont facturés dans Extrabat. Ces trois réglages se
 * modifient à l'écran ; c'est à l'utilisateur de les mettre à sa réalité.
 */
export const REGLES_PAR_DEFAUT: ReglesProjection = {
  delaiJours: 30,
  tauxTva: 0.1,
  partAcompte: 0,
};

export interface EncaissementProjete {
  chantierId: string;
  numero: string;
  libelle: string;
  client: string;
  /** Fin de l'intervention (yyyy-mm-dd). */
  finIntervention: string;
  montantHt: number;
  /** Montant attendu sur le compte : TTC, net de l'acompte supposé. */
  montant: number;
  /** Date à laquelle l'argent est attendu (yyyy-mm-dd). */
  date: string;
  etat: EtatEncaissement;
  /** Le montant vient d'une saisie, pas du chantier. */
  montantForce: boolean;
  /** La date vient d'une saisie, pas du calcul. */
  dateForcee: boolean;
  /** Le chantier ne porte aucun montant et rien n'a été saisi : montant inconnu. */
  montantInconnu: boolean;
  /** L'intervention est terminée : le travail est fait, l'argent est dû. */
  travailFait: boolean;
  /** Date attendue déjà dépassée au regard du relevé : jamais compté. */
  enRetard: boolean;
  note: string | null;
}

/**
 * Traduit les chantiers placés en encaissements datés.
 *
 * `dateSolde` est la date du dernier mouvement bancaire connu : c'est elle, et
 * non la date du jour, qui sépare ce qui est « peut-être déjà encaissé » de ce
 * qui reste à venir. Le relevé ne prouve rien au-delà d'elle.
 */
export function projeterEncaissements(
  chantiers: readonly ChantierProjetable[],
  regles: ReglesProjection,
  ajustements: ReadonlyMap<string, AjustementEncaissement>,
  dateSolde: string,
  aujourdhui: string,
): EncaissementProjete[] {
  const lignes: EncaissementProjete[] = [];
  for (const ch of chantiers) {
    const fin = ch.finIntervention.slice(0, 10);
    const aj = ajustements.get(ch.chantierId);
    const dateSaisie = aj?.datePrevue ?? null;
    const dateForcee = dateSaisie !== null;
    const date = dateSaisie ?? ajouterJoursIso(fin, regles.delaiJours);
    const montantSaisi = aj?.montantPrevu ?? null;
    const montantForce = montantSaisi !== null;
    const montant = montantSaisi ?? ch.montantHt * (1 + regles.tauxTva) * (1 - regles.partAcompte);
    lignes.push({
      chantierId: ch.chantierId,
      numero: ch.numero,
      libelle: ch.libelle,
      client: ch.client,
      finIntervention: fin,
      montantHt: ch.montantHt,
      montant: Math.round(montant * 100) / 100,
      date,
      etat: aj?.etat ?? 'attendu',
      montantForce,
      dateForcee,
      montantInconnu: !montantForce && ch.montantHt === 0,
      travailFait: fin <= aujourdhui.slice(0, 10),
      enRetard: date <= dateSolde.slice(0, 10),
      note: aj?.note ?? null,
    });
  }
  // Le plus proche d'abord : c'est l'ordre dans lequel l'argent est censé tomber.
  return lignes.sort((a, b) => a.date.localeCompare(b.date) || a.numero.localeCompare(b.numero));
}

export interface MoisProjete {
  annee: number;
  /** 0-11. */
  mois: number;
  /** Mois du dernier relevé : il est déjà entamé, son solde de départ est réel. */
  partiel: boolean;
  soldeDebut: number;
  encaissements: number;
  nbEncaissements: number;
  /** Charges qu'il reste à payer ce mois-ci. */
  decaissements: number;
  /** Charges déjà sorties du compte ce mois-ci (mouvements réels). */
  chargesDejaPayees: number;
  soldeFin: number;
  /** Ce que la grille prévisionnelle annonçait en encaissements. Repère de
   *  lecture UNIQUEMENT : jamais additionné, sous peine de compter deux fois. */
  encaissementsPrevus: number | null;
}

export interface EntreeProjection {
  /** Solde réellement constaté sur le compte, et date du mouvement qui le porte. */
  soldeConstate: number;
  dateSolde: string;
  encaissements: readonly EncaissementProjete[];
  /** Charges prévues par la grille, index 0-11. */
  chargesParMois: readonly (number | null)[];
  /** Charges déjà sorties du compte, index 0-11, d'après les mouvements réels. */
  chargesReellesParMois: readonly number[];
  /** Encaissements annoncés par la grille, index 0-11 (repère seulement). */
  encaissementsPrevusParMois: readonly (number | null)[];
  annee: number;
}

export interface Projection {
  soldeConstate: number;
  dateSolde: string;
  mois: MoisProjete[];
  /** Encaissements retenus dans le calcul du solde. */
  retenus: EncaissementProjete[];
  /** Attendus, mais leur date est déjà passée : listés, jamais comptés. */
  enRetard: EncaissementProjete[];
  /** Attendus après décembre de l'année affichée : hors tableau. */
  horsHorizon: EncaissementProjete[];
  /** Rangés à la main : déjà encaissés, ou écartés. */
  ecartes: EncaissementProjete[];
  totalRetenu: number;
  totalEnRetard: number;
  /** Index, dans `mois`, du premier mois dont le solde projeté est négatif ; -1 sinon. */
  premierMoisNegatif: number;
  /** Point bas du solde projeté, et l'index du mois où il survient. */
  creux: { solde: number; mois: number } | null;
}

/**
 * Déroule le solde du compte, du mois du dernier relevé à décembre.
 *
 * La projection s'arrête à décembre parce que la grille prévisionnelle s'y
 * arrête : au-delà, les charges sont inconnues, et un solde calculé sur les
 * seuls encaissements serait faussement rassurant.
 *
 * Le mois du relevé est traité à part : une partie de ses charges est déjà
 * sortie du compte et se trouve donc DÉJÀ dans le solde constaté. On ne retient
 * que ce qui reste à payer — la prévision du mois moins ce qui en est sorti —
 * sinon ces charges compteraient deux fois.
 */
export function projeterSolde(entree: EntreeProjection): Projection {
  const dateSolde = entree.dateSolde.slice(0, 10);
  const retenus: EncaissementProjete[] = [];
  const enRetard: EncaissementProjete[] = [];
  const horsHorizon: EncaissementProjete[] = [];
  const ecartes: EncaissementProjete[] = [];
  for (const e of entree.encaissements) {
    if (e.etat !== 'attendu') ecartes.push(e);
    else if (e.enRetard) enRetard.push(e);
    else if (anneeDe(e.date) > entree.annee) horsHorizon.push(e);
    else if (anneeDe(e.date) < entree.annee) enRetard.push(e);
    else retenus.push(e);
  }

  const parMois: EncaissementProjete[][] = Array.from({ length: 12 }, () => []);
  for (const e of retenus) parMois[moisDe(e.date)]?.push(e);

  const debut = anneeDe(dateSolde) === entree.annee ? moisDe(dateSolde) : 0;
  const mois: MoisProjete[] = [];
  let solde = entree.soldeConstate;
  for (let m = debut; m < 12; m += 1) {
    const liste = parMois[m] ?? [];
    const encaissements = liste.reduce((s, e) => s + e.montant, 0);
    const prevu = entree.chargesParMois[m] ?? 0;
    const dejaPayees = entree.chargesReellesParMois[m] ?? 0;
    // Mois du relevé : ce qui est déjà sorti est déjà dans le solde constaté.
    const partiel = m === debut && anneeDe(dateSolde) === entree.annee;
    const decaissements = partiel ? Math.max(0, prevu - dejaPayees) : prevu;
    const soldeDebut = solde;
    solde = soldeDebut + encaissements - decaissements;
    mois.push({
      annee: entree.annee,
      mois: m,
      partiel,
      soldeDebut,
      encaissements,
      nbEncaissements: liste.length,
      decaissements,
      chargesDejaPayees: partiel ? dejaPayees : 0,
      soldeFin: solde,
      encaissementsPrevus: entree.encaissementsPrevusParMois[m] ?? null,
    });
  }

  // Point bas du solde projeté : c'est lui qui dit si l'année passe ou non.
  let creux: { solde: number; mois: number } | null = null;
  for (let i = 0; i < mois.length; i += 1) {
    const x = mois[i];
    if (!x) continue;
    if (creux === null || x.soldeFin < creux.solde) creux = { solde: x.soldeFin, mois: i };
  }

  return {
    soldeConstate: entree.soldeConstate,
    dateSolde,
    mois,
    retenus,
    enRetard,
    horsHorizon,
    ecartes,
    totalRetenu: retenus.reduce((s, e) => s + e.montant, 0),
    totalEnRetard: enRetard.reduce((s, e) => s + e.montant, 0),
    premierMoisNegatif: mois.findIndex((x) => x.soldeFin < 0),
    creux,
  };
}
